import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TablePagination,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Build as BuildIcon,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import injupemplogo from './injupemplogo.png';

const ReporteriaMantenimiento = () => {
  // Estados para los datos
  const [historial, setHistorial] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [talleres, setTalleres] = useState([]);
  const [filteredHistorial, setFilteredHistorial] = useState([]);
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    vehiculo: '',
    tipo: '',
    fechaInicio: '',
    fechaFin: '',
    taller: ''
  });
  
  // Estados para UI
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [dialogFiltros, setDialogFiltros] = useState(false);

  // Estados para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Cargar vehículos
  useEffect(() => {
    const fetchVehiculos = async () => {
      try {
        const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/vehiculos/'); 
        if (response.ok) {
          const data = await response.json();
          setVehiculos(data);
        }
      } catch (error) {
        console.error('Error fetching vehículos:', error);
      }
    };

    fetchVehiculos();
  }, []);

  // Cargar historial
  useEffect(() => {
    const fetchHistorial = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/historial/'); 
        if (response.ok) {
          const data = await response.json();
          
          // Ordenar del más nuevo al más antiguo
          const sortedData = data.sort((a, b) => {
            return b.id - a.id;
          });
          
          setHistorial(sortedData);
          setFilteredHistorial(sortedData);
        } else {
          console.error('Error fetching historial data');
        }
      } catch (error) {
        console.error('Error:', error);
        setError('Error al cargar el historial de mantenimiento');
      } finally {
        setLoading(false);
      }
    };

    fetchHistorial();
  }, []);

  // Cargar talleres para mapear nombres
  useEffect(() => {
    const fetchTalleres = async () => {
      try {
        const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/taller/');
        if (response.ok) {
          const data = await response.json();
          setTalleres(data);
        }
      } catch (error) {
        console.error('Error fetching talleres:', error);
      }
    };

    fetchTalleres();
  }, []);

  // Función para obtener información completa del vehículo
  const getInfoVehiculo = (vehiculoId) => {
    if (!vehiculoId) return { placa: 'No asignado', marca: '', modelo: '' };
    
    const vehiculo = vehiculos.find(v => v.id === vehiculoId);
    return vehiculo ? {
      placa: vehiculo.placa || `Vehículo #${vehiculoId}`,
      marca: vehiculo.marca || '',
      modelo: vehiculo.modelo || '',
      año: vehiculo.año || '',
      color: vehiculo.color || ''
    } : { placa: `Vehículo #${vehiculoId}`, marca: '', modelo: '' };
  };

  // Función para obtener nombre del taller
  const getNombreTaller = (tallerId) => {
    if (!tallerId) return 'No asignado';
    
    const taller = talleres.find(t => t.id === tallerId);
    return taller ? taller.nombre : `Taller #${tallerId}`;
  };

  // Formatear fecha
  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return 'N/A';
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtered = [...historial];

    // Filtro por rango de fechas
    if (filtros.fechaInicio) {
      filtered = filtered.filter(item => {
        const fechaRealizacion = item.fecha_realizacion || item.fecha || item.fecha_creacion;
        if (!fechaRealizacion) return false;
        const fechaItem = new Date(fechaRealizacion).toISOString().split('T')[0];
        return fechaItem >= filtros.fechaInicio;
      });
    }

    if (filtros.fechaFin) {
      filtered = filtered.filter(item => {
        const fechaRealizacion = item.fecha_realizacion || item.fecha || item.fecha_creacion;
        if (!fechaRealizacion) return false;
        const fechaItem = new Date(fechaRealizacion).toISOString().split('T')[0];
        return fechaItem <= filtros.fechaFin;
      });
    }

    // Filtro por vehículo
    if (filtros.vehiculo) {
      filtered = filtered.filter(item => {
        const vehiculoId = item.vehiculo_id || item.vehiculo;
        return vehiculoId && vehiculoId.toString() === filtros.vehiculo;
      });
    }

    // Filtro por tipo
    if (filtros.tipo) {
      filtered = filtered.filter(item => {
        const tipo = item.tipo_mantenimiento || item.tipo;
        return tipo === filtros.tipo;
      });
    }

    // Filtro por taller
    if (filtros.taller) {
      filtered = filtered.filter(item => {
        const tallerId = item.taller_id || item.taller;
        return tallerId && tallerId.toString() === filtros.taller;
      });
    }

    // Mantener el orden por ID descendente
    const filteredAndSorted = filtered.sort((a, b) => b.id - a.id);
    setFilteredHistorial(filteredAndSorted);
    setPage(0);
    setDialogFiltros(false);
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      vehiculo: '',
      tipo: '',
      fechaInicio: '',
      fechaFin: '',
      taller: ''
    });
    // Restaurar el orden original por ID descendente
    const historialOrdenado = [...historial].sort((a, b) => b.id - a.id);
    setFilteredHistorial(historialOrdenado);
    setPage(0);
    setDialogFiltros(false);
  };

  // Obtener opciones únicas para los filtros
  const tiposMantenimiento = [...new Set(historial
    .map(item => item.tipo_mantenimiento || item.tipo)
    .filter(tipo => tipo != null)
  )];

  // Obtener vehículos únicos para el filtro
  const vehiculosUnicos = [...new Set(historial
    .map(item => item.vehiculo_id || item.vehiculo)
    .filter(id => id != null)
  )].map(vehiculoId => {
    const infoVehiculo = getInfoVehiculo(vehiculoId);
    return {
      id: vehiculoId,
      nombre: `${infoVehiculo.placa} - ${infoVehiculo.marca} ${infoVehiculo.modelo}`
    };
  });

  // Obtener talleres únicos para el filtro
  const talleresUnicos = [...new Set(historial
    .map(item => item.taller_id || item.taller)
    .filter(id => id != null)
  )].map(tallerId => {
    return {
      id: tallerId,
      nombre: getNombreTaller(tallerId)
    };
  });

  // Obtener estadísticas SIMPLIFICADAS - solo preventivos y correctivos
  const estadisticas = {
    total: filteredHistorial.length,
    preventivo: filteredHistorial.filter(h => {
      const tipo = h.tipo_mantenimiento || h.tipo;
      return tipo && tipo.toLowerCase() === 'preventivo';
    }).length,
    correctivo: filteredHistorial.filter(h => {
      const tipo = h.tipo_mantenimiento || h.tipo;
      return tipo && tipo.toLowerCase() === 'correctivo';
    }).length,
  };

  // Funciones de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Obtener datos paginados
  const historialPaginado = filteredHistorial.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Función para convertir imagen a Base64
  const imageToBase64 = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = reject;
      img.src = imageUrl;
    });
  };

  // Función de exportación a Excel CORREGIDA
  const exportarAExcel = async () => {
    setExporting(true);
    
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Reporte Mantenimiento INJUPEMP');

      let currentRow = 1;

      // 1. AGREGAR LOGO
      try {
        const logoBase64 = await imageToBase64(injupemplogo);
        const base64Data = logoBase64.replace(/^data:image\/png;base64,/, '');
        
        const imageId = workbook.addImage({
          base64: base64Data,
          extension: 'png',
        });
        
        worksheet.addImage(imageId, {
          tl: { col: 1, row: 0 },
          br: { col: 3, row: 2 },
        });
        
        currentRow = 3;
        
      } catch (logoError) {
        console.warn('No se pudo cargar el logo:', logoError);
        currentRow = 1;
      }

      // 2. TÍTULO PRINCIPAL
      const titleRow = worksheet.getRow(currentRow);
      titleRow.getCell(2).value = 'REPORTE DE MANTENIMIENTO VEHICULAR - INJUPEMP';
      titleRow.getCell(2).font = { 
        bold: true, 
        size: 14, 
        color: { argb: 'FF00b6dd' } 
      };
      titleRow.height = 25;
      currentRow++;

      // 3. INFORMACIÓN BÁSICA DEL REPORTE
      const infoData = [
        ['Fecha de generación:', new Date().toLocaleString('es-ES')],
        ['Total de registros:', filteredHistorial.length],
        ['Período del reporte:', `${filtros.fechaInicio || 'Inicio'} - ${filtros.fechaFin || 'Fin'}`],
        ['Filtros aplicados:', `Vehículo: ${filtros.vehiculo ? vehiculosUnicos.find(v => v.id && v.id.toString() === filtros.vehiculo)?.nombre : 'Todos'}, Tipo: ${filtros.tipo || 'Todos'}`]
      ];

      infoData.forEach(([label, value]) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(2).value = label;
        row.getCell(2).font = { bold: true };
        row.getCell(3).value = value;
        currentRow++;
      });

      currentRow++;

      // 4. ESTADÍSTICAS SIMPLIFICADAS
      const statsTitleRow = worksheet.getRow(currentRow);
      statsTitleRow.getCell(2).value = 'ESTADÍSTICAS';
      statsTitleRow.getCell(2).font = { bold: true, color: { argb: 'FF00b6dd' } };
      currentRow++;

      const statsData = [
        ['Preventivos:', estadisticas.preventivo],
        ['Correctivos:', estadisticas.correctivo],
        ['Total:', estadisticas.total]
      ];

      statsData.forEach(([label, value]) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(2).value = label;
        row.getCell(2).font = { bold: true };
        row.getCell(3).value = value;
        currentRow++;
      });

      currentRow++;

      // 5. ENCABEZADOS DE LA TABLA
      const encabezados = ['ID', 'Vehículo', 'Placa', 'Tipo Mantenimiento', 'Descripción', 'Taller', 'Fecha Realización', 'Kilometraje'];
      
      const headerRow = worksheet.getRow(currentRow);
      encabezados.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = {
          bold: true,
          color: { argb: 'FFFFFFFF' },
          size: 10
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF00b6dd' }
        };
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle'
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
      });
      headerRow.height = 20;
      currentRow++;

      // 6. AGREGAR DATOS DE LA TABLA
      const datosExcel = filteredHistorial.map(item => {
        const vehiculoInfo = getInfoVehiculo(item.vehiculo_id || item.vehiculo);
        const fechaRealizacion = item.fecha_realizacion || item.fecha || item.fecha_creacion;
        const tipoMantenimiento = item.tipo_mantenimiento || item.tipo;
        
        return {
          'ID': item.id,
          'Vehículo': `${vehiculoInfo.marca} ${vehiculoInfo.modelo}`,
          'Placa': vehiculoInfo.placa,
          'Tipo Mantenimiento': tipoMantenimiento || 'N/A',
          'Descripción': item.descripcion || 'Sin descripción',
          'Taller': getNombreTaller(item.taller_id || item.taller),
          'Fecha Realización': fechaRealizacion ? new Date(fechaRealizacion) : 'N/A',
          'Kilometraje': item.kilometraje_programado || item.kilometraje || 'N/A'
        };
      });

      if (datosExcel.length > 0) {
        datosExcel.forEach((item) => {
          const row = worksheet.getRow(currentRow);
          
          Object.values(item).forEach((value, colIndex) => {
            const cell = row.getCell(colIndex + 1);
            cell.value = value;
            
            if (value instanceof Date) {
              cell.numFmt = 'dd/mm/yyyy hh:mm';
            }
            
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
              left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
              bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
              right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
            };
          });
          
          currentRow++;
        });
      }

      // 7. CONFIGURACIÓN DE COLUMNAS
      const columnWidths = [8, 20, 12, 15, 25, 20, 18, 12];
      columnWidths.forEach((width, index) => {
        worksheet.getColumn(index + 1).width = width;
      });

      // 8. AUTO FILTROS
      if (datosExcel.length > 0) {
        worksheet.autoFilter = {
          from: { row: currentRow - datosExcel.length - 1, column: 1 },
          to: { row: currentRow - 1, column: encabezados.length }
        };
      }

      // GENERAR ARCHIVO
      const fecha = new Date();
      const fechaStr = fecha.toISOString().split('T')[0];
      const nombreArchivo = `Reporte_Mantenimiento_INJUPEMP_${fechaStr}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nombreArchivo;
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      setError('Error al exportar el reporte: ' + error.message);
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // Componente de tarjeta de estadísticas SIMPLIFICADO
  const TarjetaEstadistica = ({ titulo, valor, icono, color }) => (
    <Card sx={{ 
      bgcolor: color, 
      color: 'white', 
      textAlign: 'center',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    }}>
      <CardContent>
        {icono}
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', my: 1 }}>
          {valor}
        </Typography>
        <Typography variant="h6" component="div">
          {titulo}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Cargando reportes...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <AppBar position="static" color="primary" elevation={2}>
        <Toolbar>
          <BuildIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Reportería de Mantenimiento Vehicular
          </Typography>
          <Tooltip title="Recargar datos">
            <IconButton color="inherit" onClick={() => window.location.reload()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Estadísticas SIMPLIFICADAS - solo 3 tarjetas */}
      <Grid container spacing={3} sx={{ mt: 1, mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TarjetaEstadistica
            titulo="Total Mantenimientos"
            valor={estadisticas.total}
            icono={<BuildIcon sx={{ fontSize: 40, opacity: 0.9 }} />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TarjetaEstadistica
            titulo="Preventivos"
            valor={estadisticas.preventivo}
            icono={<CheckCircle sx={{ fontSize: 40, opacity: 0.9 }} />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TarjetaEstadistica
            titulo="Correctivos"
            valor={estadisticas.correctivo}
            icono={<Schedule sx={{ fontSize: 40, opacity: 0.9 }} />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Barra de acciones */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={8}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Resumen de Resultados
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {filteredHistorial.length} mantenimientos encontrados • 
                Preventivos: {estadisticas.preventivo} • 
                Correctivos: {estadisticas.correctivo}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setDialogFiltros(true)}
                disabled={historial.length === 0}
              >
                Filtros
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={exportarAExcel}
                disabled={exporting || filteredHistorial.length === 0}
                sx={{ minWidth: 160 }}
              >
                {exporting ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Exportando...
                  </>
                ) : (
                  'Descargar Excel'
                )}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        {exporting && <LinearProgress />}
      </Card>

      {/* Tabla de historial con paginación */}
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Vehículo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Placa</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Taller</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Fecha Realización</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Kilometraje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historialPaginado.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <FilterIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" gutterBottom>
                          No se encontraron mantenimientos
                        </Typography>
                        <Typography variant="body2">
                          {historial.length === 0 
                            ? 'No hay datos disponibles' 
                            : 'Intente ajustar los filtros de búsqueda'
                          }
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  historialPaginado.map((item) => {
                    const infoVehiculo = getInfoVehiculo(item.vehiculo_id || item.vehiculo);
                    const fechaRealizacion = item.fecha_realizacion || item.fecha || item.fecha_creacion;
                    const tipoMantenimiento = item.tipo_mantenimiento || item.tipo;
                    
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell component="th" scope="row">
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            #{item.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {infoVehiculo.marca} {infoVehiculo.modelo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {infoVehiculo.placa}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={tipoMantenimiento || 'N/A'} 
                            color={tipoMantenimiento === 'Preventivo' ? 'success' : 'warning'} 
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.descripcion || 'Sin descripción'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {getNombreTaller(item.taller_id || item.taller)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {formatearFecha(fechaRealizacion)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.kilometraje_programado || item.kilometraje ? `${item.kilometraje_programado || item.kilometraje} km` : 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginación */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredHistorial.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </CardContent>
      </Card>

      {/* Dialog de filtros */}
      <Dialog open={dialogFiltros} onClose={() => setDialogFiltros(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Filtros de Mantenimiento
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1, p: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom color="primary">
                Rango de Fechas
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fecha Inicio"
                    type="date"
                    fullWidth
                    value={filtros.fechaInicio}
                    onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fecha Fin"
                    type="date"
                    fullWidth
                    value={filtros.fechaFin}
                    onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom color="primary">
                Filtros Adicionales
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vehículo</InputLabel>
                    <Select
                      value={filtros.vehiculo}
                      label="Vehículo"
                      onChange={(e) => setFiltros({ ...filtros, vehiculo: e.target.value })}
                    >
                      <MenuItem value="">Todos los vehículos</MenuItem>
                      {vehiculosUnicos.map(vehiculo => (
                        <MenuItem key={vehiculo.id} value={vehiculo.id.toString()}>
                          {vehiculo.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={filtros.tipo}
                      label="Tipo"
                      onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {tiposMantenimiento.map(tipo => (
                        <MenuItem key={tipo} value={tipo}>
                          {tipo}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Taller</InputLabel>
                    <Select
                      value={filtros.taller}
                      label="Taller"
                      onChange={(e) => setFiltros({ ...filtros, taller: e.target.value })}
                    >
                      <MenuItem value="">Todos los talleres</MenuItem>
                      {talleresUnicos.map(taller => (
                        <MenuItem key={taller.id} value={taller.id.toString()}>
                          {taller.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={limpiarFiltros} startIcon={<ClearIcon />} color="inherit">
            Limpiar Filtros
          </Button>
          <Button onClick={aplicarFiltros} variant="contained" sx={{ minWidth: 120 }}>
            Aplicar Filtros
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReporteriaMantenimiento;